import {
  type DrawStructure,
  EntryStatusEnum,
  EventTypeEnum,
  MatchUpStatusEnum,
  MatchUpTypeEnum,
} from '@clubspark-node/clubspark-tennis-types';
import { SeedStatus, Wtn } from '@clubspark-react/ui';
import { type TFunction } from 'i18next';
import { drawDefinitionConstants, entryStatusConstants, factoryConstants } from 'tods-competition-factory';

import {
  Adornments,
  AdornmentsDraw,
  DrawAdditionalPlayerInformation,
  DrawMatchDayInformation,
  type DrawMatchDayInformationForDate,
  type DrawSettings,
  type InitialDrawDisplaySettings,
} from 'components/dialogs/draw/publicDrawDisplaySettings/types';
import {
  getConfigColorForAdornmentMark,
  getPlayerAdditionalData,
} from 'components/dialogs/draw/publicDrawDisplaySettings/utils';
import { type ParticipantWithStatus } from 'components/drawStructures/roundRobin/utils';
import { type VenueCourtsData } from 'components/overlays/teamMatchesOverlay/types';
import { RatingTypeEnum } from 'components/panels/eventsSplitDraws/selectedEvent/rankingsAndRatings/types';
import { getClientConfig } from 'config/clients';
import { type DrawDataInterface } from 'typedefs/drawTypes';
import { type MatchUpInterface, type TimeItemBaseType } from 'typedefs/store/scheduleTypes';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';
import { type Format, todsScheduleToFormat } from 'utils/dateTimeUtils';
import { isStandalone } from 'utils/environment';
import { formatPlayerName } from 'utils/participant';
import { isWithdrawal } from 'utils/statusFormatUtils';

interface TodsToNewDrawsOptions {
  drawSettings: { draw?: DrawSettings } | undefined;
  profileUrl?: string;
  organisationName?: string;
}

const { QUALIFIER } = entryStatusConstants;
const { MAIN } = drawDefinitionConstants;

export interface WtnData {
  confidence: number;
  gameZoneLower: number;
  gameZoneUpper: number;
  isRanked: boolean;
  wtnRating: number;
}

export const statusCodeMapping: Record<string, string[]> = {
  /* Walkovers */
  W1: ['inj', 'Injury'],
  W2: ['Ill', 'Illness'],
  W3: ['pc', 'Personal circumstance'],
  W4: ['tae', 'Tournament Administrative Error'],
  W5: ['Withdrawn', 'Withdrawn'],
  /* Defaults */
  DQ: ['dq', 'Disqualification for cause or ineligibility'],
  DM: ['cond', 'Misconduct before or between matches'],
  D5: ['ad', 'Failure to start match because of adult discipline'],
  D4: ['refsl', 'Refusal to start match'],
  D6: ['ns', 'Not showing up'],
  D7: ['late', 'Lateness for match'],
  DD: ['Def/Def', 'Double default'],
  D9: ['refsl', 'Refusal to continue match'],
  DI: ['med', 'Default (PEDs)'],
  DP: ['pps', 'Default (Point Penalty System)'],
  /* Retirements */
  RJ: ['inj', 'Injury'],
  RI: ['ill', 'Illness'],
  RC: ['pc', 'Personal circumstance'],
  RD: ['ad', 'Retirement because of adult discipline'],
  RU: ['elg', 'Ret. (eligible)'],
  /* Withdrawal */
  'WD.INJ': ['inj', 'Injury'],
  'WD.ILL': ['ill', 'Illness'],
  'WD.PC': ['pc', 'Personal circumstance'],
  'WD.TAE': ['Tae', 'Tournament Administrative Error'],
};

export const getWtn = (participant, isDoubles): WtnData => {
  return (
    participant?.ratings?.[isDoubles ? MatchUpTypeEnum.DOUBLES : MatchUpTypeEnum.SINGLES]?.find(
      (rating) => rating.scaleName === RatingTypeEnum.WTN,
    )?.scaleValue ??
    participant?.timeItems
      ?.filter(
        (timeItem) =>
          timeItem?.itemType === `SCALE.RATING.${isDoubles ? MatchUpTypeEnum.DOUBLES : MatchUpTypeEnum.SINGLES}.WTN`,
      )
      .sort(
        (a: TimeItemBaseType, b: TimeItemBaseType) => new Date(b.createdAt).valueOf() - new Date(a.createdAt).valueOf(),
      )[0]?.itemValue
  );
};

export const getMatchUpFromStructure = (
  structure: DrawStructure & {
    roundMatchUps: { [key: number]: MatchUpInterface[] };
  },
  matchUpId: string,
) =>
  Object.values(structure?.roundMatchUps ?? {})
    ?.flat()
    ?.find((roundMatchUp: any) => roundMatchUp?.matchUpId === matchUpId);

export const mapParticipants = (
  {
    participant,
    seedValue,
    isDoubles,
  }: {
    participant: ParticipantInterface;
    seedValue?: number;
    isDoubles?: boolean;
  },
  { drawSettings, profileUrl, organisationName }: TodsToNewDrawsOptions,
  isDoublesEventOrMatchUp?: boolean,
  qualifyingParticipantIds?: string[],
  eventId?: string,
) =>
  (isDoubles ? participant?.individualParticipants : [participant])?.flatMap((individualParticipant: any) => {
    const { participantId, person, teams, extensions } = individualParticipant ?? {};
    const profileId =
      profileUrl &&
      organisationName &&
      person?.personOtherIds.find((id: any) => id.uniqueOrganisationName === organisationName)?.personId;
    const participantWtnData = getWtn(individualParticipant, isDoublesEventOrMatchUp);
    const updatedEntryStatus = qualifyingParticipantIds?.includes?.(participant?.participantId)
      ? QUALIFIER
      : (participant as any)?.entryStatus;
    const isQualifier = updatedEntryStatus === EntryStatusEnum.QUALIFIER;
    const seed = !isQualifier ? seedValue : getQualifierSeedValue(participant, isDoubles, eventId);

    return {
      id: participantId,
      country: person?.addresses?.[0]?.countryCode,
      countryName: person?.addresses?.[0]?.countryCode,
      additionalInfo: getPlayerAdditionalData(
        person,
        teams,
        extensions,
        drawSettings?.draw?.additionalPlayerInformation ?? [DrawAdditionalPlayerInformation.NONE],
      ),
      draw: individualParticipant?.drawPosition,
      name: person
        ? formatPlayerName(person.standardGivenName, person.standardFamilyName)
        : individualParticipant?.participantName,
      seed,
      decorator: (
        <SeedStatus
          seed={seed}
          showStatus={!!drawSettings?.draw?.showEntryStatus && updatedEntryStatus !== EntryStatusEnum.DIRECT_ACCEPTANCE}
          status={updatedEntryStatus}
        />
      ),
      adornment: (
        <Wtn
          hideMark={!participantWtnData?.wtnRating}
          props={
            participantWtnData
              ? {
                  mark: { fill: getConfigColorForAdornmentMark(participantWtnData) },
                }
              : undefined
          }
        >
          {participantWtnData?.wtnRating ?? 0}
        </Wtn>
      ),
      link: profileId && `${profileUrl}${profileId}`,
    };
  }) ?? [];

export function mapSides(
  sides: any[],
  eventType: EventTypeEnum,
  { drawSettings, profileUrl, organisationName }: TodsToNewDrawsOptions,
  qualifyingParticipantIds?: string[],
  eventId?: string,
) {
  const isDoubles = eventType === EventTypeEnum.DOUBLES;

  return sides
    .map((side: any, index: number) => {
      const otherSide = sides[index === 0 ? 1 : 0];
      return {
        ...side,
        isDoubles,
        displaySideNumber:
          (side?.displaySideNumber ?? otherSide) ? (otherSide.displaySideNumber === 1 ? 2 : 1) : index + 1,
      };
    })
    .sort(({ displaySideNumber: a }: any, { displaySideNumber: b }: any) => +a - +b)
    .flatMap((side) =>
      mapParticipants(
        side,
        { drawSettings, profileUrl, organisationName },
        isDoubles,
        qualifyingParticipantIds,
        eventId,
      ),
    );
}

export const getAdornmentSetting = (drawSettings) => {
  const wtnDrawsLevelConfigExists = getClientConfig().scalingTypes.some((scaleType) => scaleType[0] === 'WTN');
  const wtnEventCategory = drawSettings?.drawData?.eventInfo?.category?.ratingType === 'WTN';
  if (
    (isStandalone() && wtnEventCategory) ||
    (wtnDrawsLevelConfigExists && drawSettings?.draw?.playerAdornment === Adornments.WTN)
  ) {
    return AdornmentsDraw.CUSTOM;
  } else {
    return AdornmentsDraw[drawSettings?.draw?.playerAdornment || Adornments.NONE];
  }
};

export const getPublicMatchDayInfo = (
  schedule: {
    allocatedCourts?: { courtId: string; venueId: string }[];
    scheduledDate: string;
    scheduledTime: any;
    venueId: any;
  },
  matchDayInfo: DrawMatchDayInformationForDate[],
  dateTimeFormats?: { dateFormat?: Format; timeFormat?: Format; dateTimeFormat?: Format },
): { scheduleFormat?: Format; venueId: string | undefined } => {
  const dateTimeFormat: Format = dateTimeFormats?.dateTimeFormat ||
    (getClientConfig()?.formats?.dateTimeFormat && 'dateTimeFormat') || {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    };
  const dateFormat: Format = dateTimeFormats?.dateFormat ||
    (getClientConfig()?.formats?.dateFormat && 'dateFormat') || { weekday: 'short', month: 'short', day: 'numeric' };
  const timeFormat: Format = dateTimeFormats?.timeFormat ||
    (getClientConfig()?.formats?.timeFormat && 'timeFormat') || { timeStyle: 'short' };
  const venueId = schedule?.venueId || schedule?.allocatedCourts?.[0].venueId;

  const matchDayInfoForDate = matchDayInfo?.find((mdi) => mdi.dates.includes(schedule?.scheduledDate));
  if (!matchDayInfoForDate) {
    // If nothing has been specifically set for this date, return defaults
    return {
      scheduleFormat: dateTimeFormat,
      venueId: venueId,
    };
  }

  const selectedInfoForDate = matchDayInfoForDate?.selectedInformation ?? [];

  const [showDate, showTime, showFacility] = [
    selectedInfoForDate.includes(DrawMatchDayInformation.DATE),
    selectedInfoForDate.includes(DrawMatchDayInformation.TIME),
    selectedInfoForDate.includes(DrawMatchDayInformation.FACILITY),
  ];

  const getScheduleFormat = () => {
    if (showDate && showTime) {
      return dateTimeFormat;
    } else if (showDate) {
      return dateFormat;
    } else if (showTime) {
      return timeFormat;
    }
    return null;
  };

  const scheduleFormat = getScheduleFormat();

  return {
    scheduleFormat,
    venueId: showFacility ? venueId : undefined,
  };
};

export function mapMatchUp(
  matchUp,
  roundIndex: number,
  eventType: EventTypeEnum,
  players: Partial<ParticipantWithStatus>[],
  venuesData: VenueCourtsData[],
  t: TFunction,
  drawSettings?: InitialDrawDisplaySettings,
  progressingSide?: number,
  timeZone?: string,
) {
  const { schedule, sides, score, matchUpId, matchUpStatus, matchUpStatusCodes, winningSide } = matchUp;
  const isBye = matchUpStatus === MatchUpStatusEnum.BYE;
  const { scheduleFormat, venueId } = drawSettings
    ? getPublicMatchDayInfo(schedule, drawSettings?.settings?.draw?.matchDayInformation ?? [])
    : {
        scheduleFormat: (getClientConfig()?.formats?.dateTimeFormat
          ? 'dateTimeFormat'
          : {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            }) as Format,
        venueId: schedule?.venueId ?? schedule?.allocatedCourts?.[0]?.venueId,
      };

  const venue = venueId && !isBye && venuesData.find((venue: any) => venue.venueId === venueId);
  const court = venue?.courtsInfo?.find((court: any) => court.courtId === schedule?.courtId);
  const venueCourtString = court ? ` - ${court?.courtName}` : '';

  const isDoubles = eventType === EventTypeEnum.DOUBLES;
  const teamSize = isDoubles ? 2 : 1;
  const emptyPlayer: null = null;
  const emptyPlayers = Array.from({ length: teamSize }).map(() => emptyPlayer);

  const dateTime = todsScheduleToFormat(schedule, timeZone, scheduleFormat, '');
  return {
    id: matchUpId,
    schedule: isBye ? '' : dateTime || t('notScheduled'),
    score:
      score?.sets?.map((set: any) => ({
        sideScores: [
          {
            ...(Number.isSafeInteger(set?.side1Score) && { score: set.side1Score }),
            ...(Number.isSafeInteger(set?.side1TiebreakScore) && { tiebreak: set.side1TiebreakScore }),
          },
          {
            ...(Number.isSafeInteger(set?.side2Score) && { score: set.side2Score }),
            ...(Number.isSafeInteger(set?.side2TiebreakScore) && { tiebreak: set.side2TiebreakScore }),
          },
        ],
        winningSide: set?.winningSide,
      })) ?? [],
    completed: factoryConstants.completedMatchUpStatuses.includes(matchUpStatus),
    sides: sides.map(
      (
        {
          participant,
          bye,
          qualifier,
          sourceDrawPositionRange,
          drawPosition,
          participantFed,
        }: {
          participant: any;
          bye?: boolean;
          qualifier?: boolean;
          sourceDrawPositionRange?: string;
          drawPosition?: number;
          participantFed?: boolean;
        },
        sideIndex: number,
      ) => {
        const participants =
          (isDoubles
            ? participant?.individualParticipants
            : participant?.participantId && [
                {
                  participantId: participant?.participantId,
                  person: participant?.person,
                  entryStatus: participant?.entryStatus,
                },
              ]
          )?.map((p) => ({
            ...(players.find((player) => player?.id === p.participantId) || emptyPlayer),
            entryStatus: p.entryStatus,
          })) ?? emptyPlayers;
        let status = ![MatchUpStatusEnum.BYE, QUALIFIER].includes(matchUpStatus) ? matchUpStatus : undefined;
        // Remap matchupStatus in case 'WITHDRAWAL'
        const remapMatchupStatus: string = isWithdrawal(matchUpStatusCodes?.[0]) ? 'WITHDRAWAL' : matchUpStatus;

        if (remapMatchupStatus === MatchUpStatusEnum.DOUBLE_WALKOVER) status = MatchUpStatusEnum.WALKOVER;
        else if (remapMatchupStatus === MatchUpStatusEnum.DOUBLE_DEFAULT) status = MatchUpStatusEnum.DEFAULTED;
        else if (bye) status = MatchUpStatusEnum.BYE;
        else if (qualifier && !participant) status = QUALIFIER;
        else if (winningSide - 1 === sideIndex) status = 'WINNER';
        else if (remapMatchupStatus !== MatchUpStatusEnum.BYE) status = remapMatchupStatus;
        const specificStatus = statusCodeMapping[matchUpStatusCodes?.[0]]?.[0];

        if (matchUpStatusCodes?.length) {
          const sideCode = matchUpStatusCodes.find(({ sideNumber }) => sideNumber === sideIndex + 1);

          if (sideCode) {
            if (
              sideCode?.matchUpStatus &&
              sideCode?.matchUpStatus !== MatchUpStatusEnum.BYE &&
              participant?.participantId &&
              !(status === 'WINNER' && sideCode.matchUpStatus === MatchUpStatusEnum.COMPLETED)
            )
              status = sideCode.matchUpStatus;
            if (
              [
                MatchUpStatusEnum.TO_BE_PLAYED,
                MatchUpStatusEnum.CANCELLED,
                MatchUpStatusEnum.ABANDONED,
                MatchUpStatusEnum.INCOMPLETE,
                MatchUpStatusEnum.WALKOVER,
              ].includes(sideCode?.previousMatchUpStatus)
            )
              status = '';
          }
        }

        return {
          players: participants.length ? participants : emptyPlayers,
          progressed: progressingSide === sideIndex,
          draw: (roundIndex === 0 || participantFed) && (sourceDrawPositionRange || drawPosition),
          fed: participantFed,
          specificStatus,
          status,
        };
      },
    ),
    venue: `${venue?.venueAbbreviation ?? ''}${venueCourtString}`,
  };
}

export function todsToNewDraws(
  drawData: DrawDataInterface,
  options: TodsToNewDrawsOptions,
  t: TFunction,
  qualifyingParticipantIds?: string[],
) {
  const { venuesData, drawId, structureId, drawsData, tournamentInfo, eventInfo } = drawData;
  const targetDraw = drawsData?.find((draw) => draw?.drawId === drawId);
  const targetStructure = targetDraw?.structures?.find((structure) => structure.structureId === structureId);
  const roundMatchUps = targetStructure?.roundMatchUps;

  const players =
    roundMatchUps &&
    Object.entries(roundMatchUps)
      .flatMap(([, draws]) =>
        draws.flatMap(({ sides }) =>
          mapSides(
            sides
              .map((side, index: number) => {
                const otherSide = sides[index === 0 ? 1 : 0];
                const getDisplaySideNumber = () => {
                  if (side?.displaySideNumber ?? otherSide) {
                    return otherSide.displaySideNumber === 1 ? 2 : 1;
                  } else {
                    return index + 1;
                  }
                };
                return {
                  ...side,
                  displaySideNumber: getDisplaySideNumber(),
                };
              })
              .sort(({ displaySideNumber: a }, { displaySideNumber: b }) => +a - +b),
            drawData.eventInfo.eventType,
            options,
            targetStructure?.stage === MAIN && qualifyingParticipantIds,
            eventInfo.eventId,
          ),
        ),
      )
      .filter((player) => player.id);

  const rounds = roundMatchUps ? Object.entries(roundMatchUps).sort(([a], [b]) => +a - +b) : [];
  return rounds.map(([roundNumber, round], index) => {
    const nextIsFeedRound = rounds[index + 1]?.[1]?.[0]?.feedRound;
    return {
      title: round[0].roundName,
      abbreviatedTitle: round[0].abbreviatedRoundName,
      double: nextIsFeedRound,
      matches: round.map((matchUp, drawIndex) => {
        const progressingDraw = roundMatchUps[+roundNumber + 1]?.[Math.floor(drawIndex / 2)];
        const progressingSide = matchUp.sides.findIndex((side) =>
          progressingDraw?.sides?.some(({ participantId }) => participantId && participantId === side.participantId),
        );
        return mapMatchUp(
          matchUp,
          index,
          drawData.eventInfo.eventType,
          players,
          venuesData,
          t,
          undefined,
          progressingSide,
          tournamentInfo?.localTimeZone,
        );
      }),
    };
  });
}

export const getDataFromDrawData = (
  drawData,
  roundIndex: number,
  matchIndex: number,
  sideIndex,
): { matchUp: MatchUpInterface; drawPosition: number } => {
  const { drawId, structureId, drawsData } = drawData;
  const targetDraw = drawsData?.find((draw: any) => draw?.drawId === drawId);
  const targetStructure = targetDraw?.structures?.find((structure: any) => structure.structureId === structureId);
  const roundMatchUps: { [key: number]: any[] } = targetStructure?.roundMatchUps ?? {};
  const matchUp = roundMatchUps?.[roundIndex + 1]?.[matchIndex];
  const drawPosition = matchUp?.sides[sideIndex]?.drawPosition;
  return { matchUp, drawPosition };
};

export const getQualifierSeedValue = (participant: ParticipantInterface, isDoubles: boolean, eventId: string) => {
  const seed = participant?.seedings?.[isDoubles ? 'DOUBLES' : 'SINGLES']?.find(
    (seed) => seed.scaleName === `q-${eventId}`,
  )?.scaleValue;
  const seedValue = parseInt(seed);

  if (seedValue && !isNaN(seedValue)) return seedValue;
  return undefined;
};
